import { useSearchParams } from "@remix-run/react";
import React from "react";
import { useSearch } from "~/context/SearchContext";
import type { Condition } from "~/types";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
}

// クエリパラメーターを生成
const generateQueryParams = (conditions: Condition) => {
  const params = new URLSearchParams();
  Object.keys(conditions).forEach((key) => {
    const value = conditions[key as keyof Condition];
    if (value.length > 0) {
      params.append(key, value.join(","));
    }
  });
  return params.toString();
};

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages }) => {
  const { conditions: selectedConditions, initialState } = useSearch();
  const [searchParams] = useSearchParams();

  const createPageUrl = (pageNumber: number) => {
    const params = new URLSearchParams(searchParams); // Copy current params
    params.set("page", pageNumber.toString());

    // Add conditions if they differ from initial state
    if (JSON.stringify(selectedConditions) !== JSON.stringify(initialState)) {
      Object.entries(selectedConditions).forEach(([key, value]) => {
        if (value.length > 0) {
          params.set(key, value.join(","));
        }
      });
    }

    return `/list?${params.toString()}`;
  };

  const conditionQueryParams =
    JSON.stringify(selectedConditions) === JSON.stringify(initialState)
      ? ""
      : `&${generateQueryParams(selectedConditions)}`;

  const createPageLinks = () => {
    const pages = [];
    const maxPagesToShow = 3; // 一度に表示するページの合計
    const halfRange = Math.floor(maxPagesToShow / 2);

    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, currentPage + halfRange);

    // 開始または終了に近づいたら調整します
    if (currentPage <= halfRange) {
      endPage = Math.min(totalPages, maxPagesToShow);
    } else if (currentPage + halfRange >= totalPages) {
      startPage = Math.max(1, totalPages - maxPagesToShow + 1);
    }

    // 必要に応じて「1」と省略記号を追加します
    if (startPage > 1) {
      pages.push(
        <li key={1}>
          <a className="page-numbers" href={createPageUrl(1)}>
            1
          </a>
        </li>
      );
      if (startPage > 2) {
        pages.push(<li key="start-ellipsis">…</li>);
      }
    }

    // ページの範囲
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i}>
          {i === currentPage ? (
            <span aria-current="page" className="page-numbers current">
              {i}
            </span>
          ) : (
            <a className="page-numbers" href={createPageUrl(i)}>
              {i}
            </a>
          )}
        </li>
      );
    }

    // 必要に応じて省略記号と最終ページを追加します
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<li key="end-ellipsis">…</li>);
      }
      pages.push(
        <li key={totalPages}>
          <a className="page-numbers" href={createPageUrl(totalPages)}>
            {totalPages}
          </a>
        </li>
      );
    }

    return pages;
  };

  return (
    <nav className="pagination" aria-label="検索結果ページネーション">
      <ul>
        <li>
          <a
            className="prev"
            href={createPageUrl(currentPage - 1)}
            aria-disabled={currentPage === 1}
          >
            <div className="">
              <svg className="">
                <use xlinkHref="/symbol-defs.svg#icon-chevron-left"></use>
              </svg>
            </div>
            <span className="">前へ</span>
          </a>
        </li>
        {createPageLinks()}
        <li>
          <a
            className="next"
            href={createPageUrl(currentPage + 1)}
            aria-disabled={currentPage === totalPages}
          >
            <span className="">次へ</span>
            <div className="">
              <svg className="">
                <use xlinkHref="/symbol-defs.svg#icon-chevron-right"></use>
              </svg>
            </div>
          </a>
        </li>
      </ul>
    </nav>
  );
};

export default Pagination;
